import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { Table, TableFilterEvent, TableModule } from 'primeng/table';
import { UserGroup } from '../../../../feature/user-management/user-group-management/model/user-group';
import { UserDirectoryCompany as Company } from '../../../model/user/user-directory-company';
import { TableFilterIconComponent } from '../../table-filter-icon/table-filter-icon.component';
import { canRunTableSearch, normalizeTableSearchValue, PrimeNgTableSearch } from '../../../helper/primeng-table-search';

@Component({
  selector: 'gate-user-directory-company-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ButtonModule,
    CheckboxModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    Skeleton,
    TableModule,
    TableFilterIconComponent,
  ],
  templateUrl: './user-directory-company-table.component.html',
  styleUrl: './user-directory-company-table.component.scss',
})
export class UserDirectoryCompanyTableComponent {
  @Input() companies: Company[] = [];
  @Input() captionKey = 'userAdministration.registeredCompanies';
  @Input() emptyMessageKey = 'userAdministration.noRegisteredCompaniesFound';
  @Input() addressHeaderKey = 'userAdministration.nameAddress';
  @Input() loading = false;
  @Input() isModuleAdmin = false;
  @Input() selectable = false;
  @Input() showGroupSelection = false;
  @Input() showImpersonation = false;
  @Input() showApplyAction = false;
  @Input() groupSelectionMode: 'single' | 'multi' = 'multi';
  @Input() selectedCompanies: Record<string, boolean> = {};
  @Input() selectedGroups: Record<string, UserGroup[]> = {};
  @Output() selectedCompaniesChange = new EventEmitter<Record<string, boolean>>();
  @Output() selectedGroupsChange = new EventEmitter<Record<string, UserGroup[]>>();
  @Output() companySelectionChange = new EventEmitter<Company>();
  @Output() groupSelectionChange = new EventEmitter<{ company: Company; selected: UserGroup[] }>();
  @Output() toggleAllSelection = new EventEmitter<boolean>();
  @Output() impersonate = new EventEmitter<Company>();
  @Output() applyCompany = new EventEmitter<Company>();
  public globalFilterValue = '';
  private hasColumnFilter = false;
  private globalFilterDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  // Whether the search box or any column filter currently has a value - drives the
  // Clear Filter button's visibility.
  public hasActiveFilterOrSearch(): boolean {
    return !!this.globalFilterValue || this.hasColumnFilter;
  }

  public onFilter(event: TableFilterEvent): void {
    this.hasColumnFilter = Object.entries(event.filters ?? {}).some(([field, meta]) => {
      if (field === 'global') {
        return false;
      }
      const value = Array.isArray(meta) ? meta[0]?.value : (meta as { value?: unknown })?.value;
      return value !== null && value !== undefined && value !== '';
    });
  }

  public onGlobalFilterChange(value: string, table: Table): void {
    const searchValue = normalizeTableSearchValue(value);
    this.globalFilterValue = value;

    if (this.globalFilterDebounceTimer) {
      clearTimeout(this.globalFilterDebounceTimer);
    }

    this.globalFilterDebounceTimer = setTimeout(() => {
      if (!canRunTableSearch(searchValue)) {
        return;
      }

      table.filterGlobal(searchValue, 'contains');
    }, PrimeNgTableSearch.debounceMs);
  }

  public areAllCompaniesSelected(): boolean {
    if (this.companies.length === 0) {
      return false;
    }
    return this.companies.every((company) => !!this.selectedCompanies[company.rs2CompanyNo]);
  }

  public onToggleAll(checked: boolean): void {
    this.toggleAllSelection.emit(checked);
  }

  public clearFilters(table: { clear: () => void }): void {
    this.globalFilterValue = '';
    this.hasColumnFilter = false;
    table.clear();
  }

  public onCompanySelectionChange(company: Company): void {
    this.selectedCompaniesChange.emit(this.selectedCompanies);
    this.companySelectionChange.emit(company);
  }

  public onGroupSelectionChange(company: Company, selected: UserGroup[]): void {
    this.selectedGroupsChange.emit(this.selectedGroups);
    this.groupSelectionChange.emit({ company, selected });
  }

  public getSelectedGroup(company: Company): UserGroup | null {
    return this.selectedGroups[company.rs2CompanyNo]?.[0] ?? null;
  }

  public onSingleGroupSelectionChange(company: Company, selected: UserGroup | null): void {
    this.selectedGroups[company.rs2CompanyNo] = selected ? [selected] : [];
    this.onGroupSelectionChange(company, this.selectedGroups[company.rs2CompanyNo]);
  }

  public getShortName(company: Company): string {
    return company.shortName || company.name || '-';
  }

  public getAddressTitle(company: Company): string {
    return [
      company.name,
      company.address.streetAddress,
      company.address.zipCode + " " + company.address.city + (company.address.country?", " + company.address.country:""),
    ].filter(Boolean).join('\n');
  }
}
